import {
  LayersIcon,
  TrendingUpIcon,
  FlameIcon,
  BatteryChargingIcon,
  ClipboardListIcon,
  FlagIcon,
  CalendarDaysIcon,
  ZapIcon,
  ClipboardCheckIcon,
  ActivityIcon,
} from 'lucide-react';

export const phases = [
  {
    n: '01',
    name: 'Les Fondations',
    short: 'Construisez votre base.',
    icon: LayersIcon,
    lead: 'Construisez la base de votre préparation.',
    desc: "Cette première phase vous aide à comprendre les exigences du triathlon distance complète et à établir une organisation adaptée à votre point de départ.",
    learn: [
      'Évaluer votre niveau de départ.',
      'Définir des objectifs réalistes.',
      "Organiser votre temps d'entraînement.",
      'Comprendre le rôle des trois disciplines.',
      "Poser les bases d'une progression régulière.",
    ],
  },
  {
    n: '02',
    name: 'Développer son Endurance',
    short: 'Développez votre endurance.',
    icon: TrendingUpIcon,
    lead: 'Développez votre capacité à soutenir un effort prolongé.',
    desc: "Cette phase vous aide à organiser vos séances et à comprendre comment les trois disciplines s'intègrent dans votre préparation.",
    learn: [
      'Structurer vos séances de natation.',
      'Organiser vos entraînements de vélo.',
      'Planifier vos séances de course à pied.',
      'Comprendre les principes de progression de la charge.',
      'Intégrer les périodes de récupération à votre organisation.',
    ],
  },
  {
    n: '03',
    name: 'Le Carburant du Triathlète',
    short: 'Préparez votre stratégie nutritionnelle.',
    icon: FlameIcon,
    lead: "Comprenez les principes du ravitaillement d'endurance.",
    desc: "Cette phase aborde les principes nutritionnels et d'hydratation associés aux efforts prolongés.",
    learn: [
      "Comprendre le rôle des glucides dans l'endurance.",
      "Organiser votre alimentation avant et après l'effort.",
      'Réfléchir au ravitaillement pendant les longues séances.',
      "Comprendre les facteurs qui influencent l'hydratation.",
      'Tester et évaluer progressivement votre stratégie.',
    ],
  },
  {
    n: '04',
    name: 'Récupération et Durabilité',
    short: 'Intégrez la récupération à votre préparation.',
    icon: BatteryChargingIcon,
    lead: 'Apprenez à intégrer la récupération dans votre préparation.',
    desc: 'Cette phase vous aide à comprendre la relation entre entraînement, fatigue et récupération.',
    learn: [
      'Organiser vos périodes de récupération.',
      'Comprendre le rôle du sommeil.',
      'Suivre votre fatigue ressentie.',
      'Intégrer le renforcement complémentaire.',
      'Adapter votre organisation lorsque la récupération devient une priorité.',
    ],
  },
  {
    n: '05',
    name: 'Préparer son Grand Jour',
    short: 'Préparez votre arrivée sur la ligne de départ.',
    icon: ClipboardListIcon,
    lead: 'Organisez les dernières étapes avant votre compétition.',
    desc: 'Cette phase vous aide à structurer les aspects pratiques de votre préparation à mesure que le jour de la course approche.',
    learn: [
      "Comprendre les principes de l'affûtage.",
      'Préparer votre équipement.',
      'Organiser vos déplacements et votre logistique.',
      'Vérifier les informations officielles de votre épreuve.',
      'Préparer votre checklist de course.',
    ],
  },
  {
    n: '06',
    name: 'Réussir sa Course',
    short: 'Transformez votre préparation en stratégie de course.',
    icon: FlagIcon,
    lead: "Transformez votre préparation en stratégie d'exécution.",
    desc: 'Cette dernière phase rassemble les éléments essentiels à prendre en compte le jour de votre triathlon.',
    learn: [
      "Réfléchir à votre stratégie d'allure.",
      'Organiser les transitions.',
      'Préparer votre stratégie de ravitaillement.',
      'Anticiper les imprévus.',
      "Évaluer votre course après l'arrivée.",
    ],
  },
];

export const tools = [
  {
    verb: 'Planifier',
    name: "Le Calendrier d'entraînement triathlon — 40 semaines™",
    desc: "Votre feuille de route visuelle pour organiser les grandes étapes de votre préparation jusqu'au jour de la course.",
    icon: CalendarDaysIcon,
  },
  {
    verb: 'Ravitailler',
    name: 'Le Guide pratique du ravitaillement triathlète™',
    desc: "Un guide pratique pour organiser votre préparation nutritionnelle et votre hydratation pendant les entraînements d'endurance.",
    icon: ZapIcon,
  },
  {
    verb: 'Préparer',
    name: 'La Checklist de la semaine de course™',
    desc: 'Une checklist pour organiser votre équipement, vos déplacements et les derniers préparatifs avant votre compétition.',
    icon: ClipboardCheckIcon,
  },
  {
    verb: 'Suivre',
    name: 'Le Carnet de suivi du triathlète™',
    desc: "Un outil pratique pour enregistrer vos séances, suivre votre régularité et observer l'évolution de votre préparation.",
    icon: ActivityIcon,
  },
];

export type BonusAccent = 'electric' | 'perf' | 'ember' | 'aqua';

export const bonuses: {
  n: string;
  accent: BonusAccent;
  title: string;
  subtitle: string;
  desc: string[];
  tags: string[];
  price: string;
  cover: string;
}[] = [
  {
    n: '01',
    accent: 'electric',
    title: "Le Calendrier d'entraînement triathlon — 40 semaines™",
    subtitle: "Votre feuille de route visuelle jusqu'au jour de la course.",
    desc: [
      "Arrêtez de considérer votre préparation comme une succession de semaines isolées.",
      'Ce calendrier vous aide à visualiser les grandes étapes de votre parcours et à organiser votre préparation sur la durée.',
      "Utilisez-le pour visualiser votre progression, organiser vos objectifs et garder une vue d'ensemble de votre préparation.",
    ],
    tags: [
      'Semaines 1–8 · Les fondations',
      'Semaines 9–20 · Développer son endurance',
      'Semaines 21–30 · Préparation spécifique',
      'Semaines 31–35 · Phase de pic',
      'Semaines 36–40 · Affûtage',
    ],
    price: '32 €',
    cover: 'https://cdn.magicpatterns.com/uploads/b27r1S4jcbNUbvivpBk5b7/Carnet_de_seguimiento_del_triatleta_(3).webp',
  },
  {
    n: '02',
    accent: 'perf',
    title: 'Le Guide pratique du ravitaillement triathlète™',
    subtitle: "Votre guide pratique pour organiser votre ravitaillement d'endurance.",
    desc: [
      "L'entraînement demande de l'énergie. Les efforts prolongés nécessitent également une réflexion sur le ravitaillement et l'hydratation.",
      'Ce guide vous aide à organiser les principaux aspects de votre préparation nutritionnelle.',
    ],
    tags: [
      "Alimentation quotidienne",
      "Nutrition avant l'effort",
      'Hydratation et électrolytes',
      "Ravitaillement pendant l'entraînement",
      "Nutrition après l'effort",
      'Ravitaillement pour la course',
    ],
    price: '29 €',
    cover: 'https://cdn.magicpatterns.com/uploads/rQZqQXbu4aGbtbHcfaB5hU/Carnet_de_seguimiento_del_triatleta_(2).webp',
  },
  {
    n: '03',
    accent: 'ember',
    title: 'La Checklist de la semaine de course™',
    subtitle: 'Votre checklist pour organiser les sept derniers jours avant le départ.',
    desc: [
      'La dernière semaine ne devrait pas être une succession de décisions prises à la dernière minute.',
      'Cette checklist vous aide à organiser les éléments pratiques de votre préparation.',
    ],
    tags: [
      'Planification et logistique',
      'Déplacements et documents',
      'Équipement',
      'Natation',
      'Vélo',
      'Course à pied',
      'Matin de la course',
      'Dernières vérifications',
    ],
    price: '32 €',
    cover: 'https://cdn.magicpatterns.com/uploads/otYcsv2UfwJwJmZE2Z7u5p/Carnet_de_seguimiento_del_triatleta_(1).webp',
  },
  {
    n: '04',
    accent: 'aqua',
    title: 'Le Carnet de suivi du triathlète™',
    subtitle: 'Suivez vos séances. Observez votre progression. Restez régulier.',
    desc: [
      "Votre entraînement devient plus facile à analyser lorsque vous disposez d'un outil pour enregistrer ce que vous faites.",
      'Ce carnet vous aide à organiser le suivi de votre préparation dans les trois disciplines.',
      "Utilisez-le pour conserver une trace de votre préparation et observer son évolution au fil des semaines.",
    ],
    tags: ['Natation', 'Vélo', 'Course à pied', "Charge d'entraînement", 'Récupération', 'Régularité hebdomadaire'],
    price: '27 €',
    cover: 'https://cdn.magicpatterns.com/uploads/8W8RGgkwqsqmEU2Tu1DKKS/Carnet_de_seguimiento_del_triatleta.webp',
  },
];

export const idealFor = [
  'Les triathlètes qui préparent une épreuve distance complète.',
  "Les sportifs d'endurance qui souhaitent structurer leur préparation.",
  'Les athlètes qui veulent mieux organiser la natation, le vélo et la course à pied.',
  'Les débutants qui recherchent une feuille de route claire.',
  'Les sportifs expérimentés qui souhaitent mieux organiser leur préparation.',
];

export const offerIncludes = [
  'Les Fondations',
  'Développer son Endurance',
  'Le Carburant du Triathlète',
  'Récupération et Durabilité',
  'Préparer son Grand Jour',
  'Réussir sa Course',
  'Organisation natation, vélo et course à pied',
  'Principes de ravitaillement et hydratation',
  'Outils de préparation à la course',
  'Les quatre bonus pratiques',
];
